import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { User, Phone, ArrowRight, AlertCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientLoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    phone: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!formData.email || !formData.phone) {
      setError("Please enter both email and phone number");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/client/login`, {
        email: formData.email.toLowerCase().trim(),
        phone: formData.phone.trim()
      });
      
      if (response.data.success) {
        // Store client session
        localStorage.setItem("clientEmail", formData.email.toLowerCase().trim());
        localStorage.setItem("clientPhone", formData.phone.trim());
        localStorage.setItem("clientAuth", "true");
        navigate("/my-quotes");
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setError("No quotes found with these details. Please check your email and phone number.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12 bg-[#f8f7f5]">
      <Helmet>
        <title>View Your Quotes | Expert Carpenter</title>
        <meta name="description" content="Log in to view your quote requests and their status." />
      </Helmet>
      
      <Card className="w-full max-w-md p-6 sm:p-8 bg-white shadow-lg">
        <div className="text-center mb-6 sm:mb-8">
          <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#2D5A4A] rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#3A3A3A]">View Your Quotes</h1>
          <p className="text-gray-500 mt-2 text-sm sm:text-base">
            Enter the email and phone number you used when requesting a quote
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Email Address
            </label>
            <Input
              type="email"
              placeholder="your@email.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="h-12 sm:h-14 text-base"
              data-testid="client-email-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Phone Number
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="tel"
                placeholder="07xxx xxxxxx"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="h-12 sm:h-14 pl-10 text-base"
                data-testid="client-phone-input"
              />
            </div>
          </div>
          
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
          
          <Button 
            type="submit" 
            className="w-full h-12 sm:h-14 bg-[#2D5A4A] hover:bg-[#234839] text-base sm:text-lg font-semibold"
            disabled={loading}
            data-testid="client-login-btn"
          >
            {loading ? "Checking..." : (
              <>
                View My Quotes
                <ArrowRight className="ml-2 h-5 w-5" />
              </>
            )}
          </Button>
        </form>
        
        <p className="mt-6 text-center text-sm text-gray-500">
          Need to request a quote?{" "}
          <a href="/contact" className="text-[#2D5A4A] font-semibold hover:underline">
            Get in touch
          </a>
        </p>
      </Card>
    </div>
  );
};

export default ClientLoginPage;
